import React, { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Button } from "../ui/Button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FileUpload } from "primereact/fileupload";
import {
  CreateInvoiceDto,
  InvoiceStatusEnum,
  InvoiceDocumentDto,
} from "../../types/invoice.types";
import {
  faPencil,
  faTrash,
  faFile,
  faDownload,
  faPlus,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClientSelector } from "./ClientSelector";
import { CurrencySelector } from "./CurrencySelector";
import { IndustrySelector } from "./IndustrySelector";
import { getAllItems } from "../../service/item/ItemServices";
import { useDispatch, useSelector } from "react-redux";
import { getAllCurrencies } from "../../service/currency/currencyServices";
import { getAllIndustries } from "../../service/industry/IndustryServices";
import { getAllTemplates } from "../../service/template/TemplateServices";
import { ListBox } from "primereact/listbox";
import { OverlayPanel } from "primereact/overlaypanel";
import { RadioButton } from "primereact/radiobutton";
import { SendInvoiceDialog } from "../ui/SendInvoiceDialog";
import { getAllPaymentTerms } from "../../service/paymentTerms/PaymentTermsService";
import {
  CreateGSTRate,
  createGSTRatesInGSTTreatment,
  getAllBusinessTypes,
  getAllGSTRatesByName,
  isSameExistByNameOrVal,
} from "../../service/businessType/BusinessTypeService";
import { getTenantPaymentSettingsByTenantId } from "../../service/tenantPaymentSettings/TenantPaymentSettingsService";
import { Tag } from "primereact/tag";
import { getAllSACDetails } from "../../service/sacDetails/SacDetailsService";
import { getAllTDSDetails } from "../../service/TDSDetails/TDSDetailsService";
import { getAllTCSDetails } from "../../service/TCSDetails/TCSDetailsService";
import { findSettingsByUserIdAndKey } from "../../service/common/commonService";
import {
  ALLOWED_EXTENSIONS,
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
} from "../../shared/constant/FileValidation";
import { showToast } from "../../store/slices/toastSlice";
import { CalculateInvoice } from "../../shared/functions/CalculateInvoice";
import {
  getDefaultSequence,
  isDuplicateInvoiceNumberExist,
} from "../../service/invoice/InvoiceServices";
import { Toast } from "primereact/toast";
import { X } from "lucide-react";
import { updateClientGSTTreatment } from "../../service/client/ClientServices";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { useNavigate } from "react-router-dom";
import {
  calculateGstLabelsAndTax,
  GstResult,
} from "../../shared/functions/calculateGstLabelsAndTax";
import { countryList } from "../../shared/constant/CountryList";
import { useCheckFeature } from "../../shared/functions/CheckFeature";

type Country = { code: string; name: string };

interface InvoiceFormProps {
  onSubmit: (data: CreateInvoiceDto, skipNavigate?: boolean) => void;
  isSubmitting: boolean;
  initialData?: any;
  mode: "create" | "edit";
  preselectedClient?: any;
}

interface FileUploadHandlerEvent {
  files: File[];
  options: {
    clear: () => void;
  };
}

export const InvoiceForm: React.FC<InvoiceFormProps> = ({
  initialData = {},
  onSubmit,
  mode,
  preselectedClient,
}) => {
  const allTaxTypes = [
    { name: "CGST", code: "CGST" },
    { name: "SGST", code: "SGST" },
    { name: "IGST", code: "IGST" },
    { name: "UTGST", code: "UTGST" },
    { name: "Other", code: "Other" },
  ];
  const toast = useRef<Toast>(null);
  const [emailTo, setEmailTo] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<{
    [key: number]: boolean;
  }>({});
  const [itemErrors, setItemErrors] = useState<Record<number, string>>({});

  const [isEditable, setIsEditable] = useState(true);
  const [selectedGstOption, setSelectedGstOption] = useState<{
    label: string;
    value: number;
  } | null>(null);
  const localization = useSelector((state: any) => state.localization);
  const [withPaymentLink, setWithPaymentLink] = useState(true);
  const [emailCc, setEmailCc] = useState<string>("");
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailMessage, setEmailMessage] = useState<string>("");
  const [residentStatus, setResidentStatus] = useState<number | null>(null);
  const [showNewTaxDialog, setShowNewTaxDialog] = useState<boolean>(false);
  const [newTaxErrors, setNewTaxErrors] = useState({
    taxName: false,
    taxRate: false,
    taxType: false,
  });
  const [invoiceData, setInvoiceData] = useState<CreateInvoiceDto>({
    client_id:
      initialData.client_id ||
      (preselectedClient?.id ? preselectedClient.id : null),
    clientName:
      initialData.clientName ||
      (preselectedClient?.name ? preselectedClient.name : ""),
    issue_date: initialData.issue_date
      ? new Date(initialData.issue_date)
      : new Date(),
    due_date:
      new Date(initialData.due_date) ||
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    currency_id: initialData.currency_id || 1,
    currency_code: initialData.currency_code || "USD",
    exchange_rate_id: initialData.exchange_rate_id || null,
    status: InvoiceStatusEnum.DRAFT,
    industry_id: initialData.industry_id || null,
    tax_inclusive: initialData.tax_inclusive || false,
    payment_terms: initialData.payment_terms || "Net 30",
    payment_term_id: initialData.payment_term_id || 1,
    business_type_id: initialData.business_type,
    notes: initialData.notes || "",
    terms_conditions: initialData.terms_conditions || "",
    template_id: initialData.template_id || null,
    is_recurring: initialData.is_recurring || false,
    invoice_items: initialData.invoice_items || [],
    business_type: initialData.buisness_type || "",
    emailTo: emailTo
      .split(",")
      .map((email) => email.trim()) // Trim spaces
      .filter((email) => email !== ""),
    emailCc: emailCc
      .split(",")
      .map((email) => email.trim()) // Trim spaces
      .filter((email) => email !== ""),
    emailSubject: emailSubject,
    emailMessage: emailMessage,
    natureOfPayment: initialData.natureOfPayment || null,
    threashold: initialData.threashold || null,
    gstType: initialData.gstType || null,
    yearOfGst: initialData.yearOfGst || null,
    gstRate: initialData.gstRate || null,
    documents: initialData.documents || [],
    invoiceNumber: initialData.invoiceNumber || initialData?.invoice_number,
    round_off: initialData?.round_off || 0,
  });

  const [groupedItems, setGroupItems] = useState<GstResult[] | []>([]);

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [selectedTaxType, setSelectedTaxType] = useState<string>(
    initialData.gstType || "TDS"
  );
  const [selectedTaxRate, setSelectedTaxRate] = useState<any>(null);
  const fileUploadRef = useRef<any>(null);

  const [selectedClient, setSelectedClient] = useState<any>(
    preselectedClient || null
  );
  const [subtotal, setSubtotal] = useState<number>(0);
  const [taxTotal, setTaxTotal] = useState<number>(initialData.tax_total || 0);
  const [total, setTotal] = useState<number>(0);
  const dispatch = useDispatch();
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [taxRates, setTaxRates] = useState<any[]>([]);
  const [value, setValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const overlayRef = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [businessTypes, setBusinessTypes] = useState<
    { label: string; value: number; description: string }[]
  >([]);
  const [paymentTerms, setPaymentTerms] = useState<any>();
  const [fileUploadError, setFileUploadError] = useState<string>("");
  const [totalDiscount, setTotalDiscount] = useState<number>(0);
  const [sacTotal, setSacTotal] = useState<number>(0);
  const [invoiceNumber, setInvoiceNumber] = useState<string>(
    initialData.invoiceNumber || initialData?.invoice_number || ""
  );
  const [isCheckingDuplicate, setIsCheckingDuplicate] =
    useState<boolean>(false);
  const [isDuplicateNumber, setIsDuplicateNumber] = useState<boolean>(false);
  const [showTaxConfig, setShowTaxConfig] = useState(false);
  const [gstin, setGstin] = useState("");
  const [makeGstinPermanent, setMakeGstinPermanent] = useState(false);
  const [selectedBusinessTypeData, setSelectedBusinessTypeData] = useState<{
    label: string;
    value: number;
    description: string;
  } | null>(null);
  const [gstRateOptions, setGstRateOptions] = useState<
    { label: string; value: number }[] | []
  >([]);
  const [newTaxData, setNewTaxData] = useState({
    name: "",
    rate: "",
    type: "",
  });
  const [digitalServices, setDigitalServices] = useState(false);
  const navigate = useNavigate();
  const [showRoundoff, setShowRoundoff] = useState(() => {
    const roundoff = parseFloat(initialData?.round_off);
    return !isNaN(roundoff) && roundoff !== 0;
  });

  const [roundoffValue, setRoundoffValue] = useState<number>(
    initialData?.round_off || 0
  );
  const hasSacFeature = useCheckFeature("SAC");

  const [isLoadingTaxRates, setIsLoadingTaxRates] = useState<boolean>(true);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);
  const [sendDialogVisible, setSendDialogVisible] = useState<boolean>(false);

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);


  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [allSACDetails, setAllSACDetails] = useState<any[]>([]);
  const searchBoxRef = useRef<any>(null);

  //#region all Functions

  //Get gst rate options
  const getAllGstRateOptions = async () => {
    if (selectedBusinessTypeData?.label) {
      const input = {
        businessTypeName: selectedBusinessTypeData?.label,
      };
      const gstRateResponse = await getAllGSTRatesByName(dispatch, input);
      setGstRateOptions(
        gstRateResponse.gstRates.map((gst: any) => {
          const prefix = gst.name.replace(/[0-9]/g, ""); // Removes digits ? IGST0 ? IGST
          const number = gst.name.replace(/\D/g, ""); // Removes non-digits ? IGST0 ? 0
          // const label = `${prefix} (${number}%)`;
          const label = `${gst?.name} (${gst?.value}%)`;
          return {
            label,
            value: gst.value, // or gst.value if you prefer
          };
        })
      );
    }
  };

  // Add function to check for duplicate invoice numbers
  const checkDuplicateInvoiceNumber = async (number: string) => {
    if (!number) return;

    setIsCheckingDuplicate(true);
    try {
      const isDuplicate = await isDuplicateInvoiceNumberExist(dispatch, number);
      setIsDuplicateNumber(isDuplicate);

      if (isDuplicate) {
        dispatch(
          showToast({
            severity: "warn",
            summary: localization?.Warning || "Warning",
            detail:
              localization?.DuplicateInvoiceNumber ||
              "This invoice number already exists",
          })
        );
      }
    } catch (error) {
      console.error("Error checking duplicate invoice number:", error);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  // Add handler for invoice number changes
  const handleInvoiceNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setInvoiceNumber(value);
    setInvoiceData((prev) => ({
      ...prev,
      invoiceNumber: value,
    }));
  };




  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const response = await getAllCurrencies(dispatch, {
        Page: 1,
        Limit: 100,
      });

      if (response) {
        const formattedCurrencies = response?.List?.map((currency: any) => ({
          id: currency.currency_id,
          code: currency.currency_code,
          name: currency.currency_name,
          symbol: currency.currency_symbol,
          label: `${currency.currency_code} - ${currency.currency_name}`,
          value: currency.currency_code,
        }));

        setCurrencies(formattedCurrencies);
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    } finally {
      setLoading(false);
    }
  };



  const getResidentStatus = async () => {
    const storedUserId = localStorage.getItem("userId");
    if (!storedUserId) {
      return "UserId is not available,unable to fetch TDS details";
    }
    const userId = JSON.parse(storedUserId);
    const input = {
      userId,
      key: "residentStatus",
    };
    const userSettings = await findSettingsByUserIdAndKey(dispatch, input);
    setResidentStatus(userSettings?.value);
    return userSettings;
  };

  const getTdsOrTcsDetails = async () => {
    try {
      let response;

      if (selectedTaxType === "TDS") {
        const userSettings = await getResidentStatus();
        const residentStatusInput = {
          residentStatus: userSettings?.value,
        };
        response = await getAllTDSDetails(dispatch, residentStatusInput);
      } else {
        response = await getAllTCSDetails(dispatch);
      }

      if (response?.length > 0) {
        const formattedTaxRates = response.map((item: any) => {
          const nature =
            selectedTaxType === "TDS"
              ? item.nature_of_payment
              : item.nature_of_collection;

          const rate =
            selectedTaxType === "TDS"
              ? item[
              item.residentStatus === "resident"
                ? "resident_rate"
                : "non_resident_rate"
              ]
              : item.gstRate;

          return {
            label: nature,
            rate: parseFloat(rate),
            threshold: parseFloat(item.threshold),
            year: item.year,
            value: item,
            rawData: item,
          };
        });
        setTaxRates(formattedTaxRates);
      }
    } catch (error) {
      console.error("Error fetching TDS/TCS details:", error);
    } finally {
      setIsLoadingTaxRates(false);
    }
  };



  const calculateTotals = () => {
    if (!invoiceData.invoice_items || invoiceData.invoice_items.length === 0) {
      setSubtotal(0);
      setTaxTotal(0);
      setTotal(0);
      return;
    }
    if (showRoundoff) {
      const { total, taxTotal, sacTotal, totalDiscount, subTotal, roundOff } =
        CalculateInvoice(invoiceData, showRoundoff);

      setSubtotal(subTotal);
      setTaxTotal(taxTotal);
      setTotal(total);
      setTotalDiscount(totalDiscount);
      setSacTotal(sacTotal);
      setInvoiceData((prev) => ({
        ...prev,
        round_off: roundOff,
      }));
      // }
    } else {
      //calculate
      const { total, taxTotal, subTotal, totalDiscount, sacTotal, roundOff } =
        CalculateInvoice(invoiceData);

      setSubtotal(subTotal);
      setTaxTotal(taxTotal);
      setTotal(total);
      setTotalDiscount(totalDiscount);
      setSacTotal(sacTotal);
      setRoundoffValue(0);
      setInvoiceData((prev) => ({
        ...prev,
        round_off: 0,
      }));
    }
  };

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: `${localization?.File || "File"} "${file.name}" ${localization?.FileUploadError ||
          "is too large. Maximum size allowed is"
          } ${MAX_FILE_SIZE / (1024 * 1024)}MB.`,
      };
    }

    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return {
        isValid: false,
        error: `${localization?.File || "File"} "${file.name}" ${localization?.FileUploadError ||
          "has an unsupported format. Only Excel, PDF, Word, and PowerPoint files are allowed."
          }`,
      };
    }

    const allowedMimeTypes = Object.keys(ALLOWED_FILE_TYPES);
    const isValidMimeType =
      allowedMimeTypes.includes(file.type) ||
      ALLOWED_EXTENSIONS.includes(fileExtension);

    if (!isValidMimeType && file.type !== "") {
      return {
        isValid: false,
        error: `${localization?.File || "File"} "${file.name}" ${localization?.FileUploadError ||
          "has an invalid file type. Only Excel, PDF, Word, and PowerPoint files are allowed."
          }`,
      };
    }

    return { isValid: true };
  };

  const getDueDateByPaymentTerm = (
    paymentTermid: number,
    currentDate = invoiceData.issue_date
  ) => {
    const paymentTermId = paymentTermid;
    const paymentTermLabel =
      paymentTerms?.find(
        (term: any) =>
          term.value === paymentTermId || term.label === paymentTermId
      )?.label || "Net 30";
    const parts = paymentTermLabel.split(" ");
    const daysToAdd = parts.length > 1 ? parseInt(parts[1]) : 30;
    currentDate = currentDate ? new Date(currentDate) : new Date();
    const dueDate = new Date(currentDate);
    dueDate.setDate(dueDate.getDate() + (isNaN(daysToAdd) ? 30 : daysToAdd));
    return dueDate;
  };

  const handleClientChange = (e: any) => {
    const paymentTermId = initialData.payment_term_id
      ? parseInt(initialData.payment_term_id)
      : e.value?.payment_terms;
    const payemntTermOfClient = paymentTerms?.find(
      (term: { label: string; value: number }) => term.value === paymentTermId
    ).label;
    const dueDate = initialData.due_date
      ? new Date(initialData.due_date)
      : getDueDateByPaymentTerm(paymentTermId);
    const currencyCode = initialData.currency_code
      ? initialData.currency_code
      : e.value?.currency_code;
    setSelectedClient(e.value);

    const selectedBusinessType = businessTypes.find(
      (val) => val.value === e.value?.
        business_type
    );
    if (selectedBusinessType) {
      setSelectedBusinessTypeData(selectedBusinessType);
    }

    setEmailTo(e.value.email);
    setInvoiceData((prev) => ({
      ...prev,
      client_id: e.value?.id,
      clientName: e.value?.name,
      currency_code: currencyCode,
      currency_id: e.value?.currency_id,
      payment_term_id: paymentTermId,
      payment_terms: payemntTermOfClient,
      business_type_id: e.value?.
        business_type || e.value?.business_type_id,
      business_type: businessTypes.find(
        (val) => val.value === e.value?.
          business_type
      )?.label,
      industry_id: e.value?.industry_id,
      due_date: dueDate,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setInvoiceData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isSameGSTExist = async (): Promise<boolean> => {
    const rate = parseFloat(newTaxData.rate);
    if (newTaxData.name && rate && invoiceData.business_type_id) {
      const input: CreateGSTRate = {
        gstTreatmentId: invoiceData.business_type_id,
        gstRateName: newTaxData.name,
        gstNameVal: rate,
        tax_type: newTaxData.type,
      };
      const isExist = await isSameExistByNameOrVal(dispatch, input);
      if (isExist) {
        toast.current?.show({
          severity: "warn",
          summary: "Already Exist!",
          detail: "Gst rate already exist",
          life: 3000,
        });
        return false;
      }
      return true;
    }
    return false;
  };

  const handleDropdownChange = (e: any, fieldName?: string) => {
    const name = fieldName || e.originalEvent?.target?.name;

    if (name === "currency") {
      const selectedCurrency = currencies?.find((c) => c.value === e.value);

      const updatedItems = invoiceData.invoice_items?.map((item) => {
        if (item.unit_price === undefined || item.unit_price === null)
          return item;

        return {
          ...item,
          unit_price: 0,
        };
      });

      setInvoiceData((prev) => ({
        ...prev,
        currency_id: selectedCurrency.id,
        currency_code: selectedCurrency?.code || "USD",
        invoice_items: updatedItems,
      }));
    } else if (name === "payment_terms") {
      const dueDate = getDueDateByPaymentTerm(e.value);

      setInvoiceData((prev) => ({
        ...prev,
        payment_terms: e.value,
        payment_term_id: e.value,
        due_date: dueDate,
      }));
    } else if (name === "industry_id") {
      setInvoiceData((prev) => ({
        ...prev,
        industry_id: e.value,
      }));
    } else if (name === "currencyCode") {
      setInvoiceData((prev) => ({
        ...prev,
        currency_code: e.value,
      }));
    } else if (name === "business_type") {
      setGroupItems([]);
      const updatedItems = invoiceData.invoice_items?.map((item) => {
        if (item.sac_gst_rate === undefined || item.sac_gst_rate === null)
          return item;

        return {
          ...item,
          sac_gst_rate: 0,
        };
      });
      setInvoiceData((prev) => ({
        ...prev,
        business_type_id: e.value,
        business_type: businessTypes.find((typ) => typ.value === e.value)
          ?.label,
        invoice_items: updatedItems,
      }));

      const selectedValue = e.value;
      const selectedOption = businessTypes.find(
        (bt) => bt.value === selectedValue
      );
      if (selectedOption) {
        // Store selected business type data for modal
        setSelectedBusinessTypeData(selectedOption);

        // Show tax configuration modal
        setShowTaxConfig(true);
      }
    } else {
      setInvoiceData((prev) => ({
        ...prev,
        [name || "template_id"]: e.value,
      }));
    }
  };

  const handleTaxConfigSave = async () => {
    if (!makeGstinPermanent) {
      return toast.current?.show({
        severity: "warn",
        summary: "Client not selected",
        detail:
          "Please,select the checkbox first to update client's gst treatment",
        life: 3000,
      });
    }

    if (!selectedClient) {
      return toast.current?.show({
        severity: "warn",
        summary: "Client not selected",
        detail: "Please,select a client first",
        life: 3000,
      });
    }
    const input = {
      clientId: selectedClient.id as number,
      gstTreatmentId: selectedBusinessTypeData?.value as number,
    };
    if (selectedClient.id && selectedBusinessTypeData?.value) {
      const updatedGST = await updateClientGSTTreatment(dispatch, input);
      if (updatedGST) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Success",
            detail: updatedGST as string,
            life: 3000,
          })
        );
      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: "Error",
            detail: "Unable to update client gst treatment",
            life: 3000,
          })
        );
      }
    }
    setShowTaxConfig(false);
    // Reset form
    setGstin("");
    setMakeGstinPermanent(false);
  };

  const handleTaxConfigCancel = () => {
    setShowTaxConfig(false);
    // Reset the dropdown selection
    setInvoiceData((prev) => ({
      ...prev,
      business_type_id: prev.business_type_id,
    }));
    setGstin("");
    setMakeGstinPermanent(false);
  };

  const itemTemplate = (option: any) => {
    const label = option.label;
    const isLongLabel = label.length > 40;
    const displayLabel = isLongLabel ? label.slice(0, 40) + "..." : label;
    return (
      <div
        className={`relative group p-2 border-b border-gray-100 ${selectedTaxRate ? "" : "hover:bg-gray-100"
          }`}
      >
        <div className="font-semibold text-sm text-black mb-1 whitespace-normal break-words max-w-full">
          {displayLabel}
        </div>

        {isLongLabel && (
          <div className="absolute z-50 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 top-0 left-0 transform -translate-y-full whitespace-normal max-w-xs">
            {label}
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-gray-600 space-x-3">
          <div>
            <span
              className={`font-bold px-1 py-1${option.rate === 0.1
                ? "bg-green-100 text-green-600 border-green-300"
                : "bg-orange-100 text-orange-500 border border-orange-300"
                }`}
            >
              {option?.rate?.toFixed(2)}%
            </span>
            <span>
              {localization?.Threshold || "Threshold"}: $
              {option.threshold.toLocaleString()}
            </span>
          </div>
          <span>{option.year}</span>
        </div>
      </div>
    );
  };

  // useEffect(() => {
  //   if (isInititalTaxType.current) {
  //     isInititalTaxType.current = false;
  //     return; // Skip the first run on initial load
  //   }
  //   setInvoiceData((prev) => ({
  //     ...prev,
  //     gstRate: 0,
  //   }));
  // }, [selectedTaxType]);



  const isSameOrAfter = (d1: Date, d2: Date): boolean => {
    const date1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
    const date2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
    return date1 >= date2;
  };

  const handleDateChange = (e: any, field: string) => {
    if (field === "due_date") {
      const dueDate = new Date(e.value);
      const issueDate = new Date(invoiceData.issue_date);

      if (!isSameOrAfter(dueDate, issueDate)) {
        toast.current?.show({
          severity: "warn",
          summary: "Invalid Date",
          detail: "Due date cannot be earlier than issue date.",
          life: 3000,
        });

        setInvoiceData((prev) => ({
          ...prev,
          due_date: issueDate,
          payment_terms: 5 as any,
          payment_term_id: 5,
        }));
        return;
      }

      const diffTime = dueDate.getTime() - issueDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const matchedTerm = paymentTerms.find((term: any) => {
        const labelParts = term.label.split(" ");
        const days = parseInt(labelParts[1]);
        return days === diffDays;
      });

      setInvoiceData((prev) => ({
        ...prev,
        due_date: dueDate,
        payment_terms: matchedTerm ? matchedTerm.value : 5,
        payment_term_id: matchedTerm ? matchedTerm.value : 5,
      }));
    } else {
      setInvoiceData((prev) => ({
        ...prev,
        [field]: e.value,
      }));
    }
  };

  const addInvoiceItem = () => {
    const newItem: any = {
      isNew: true,
      item_name: null,
      item_code: null,
      sacCode: null,
      sac_gst_rate: 0,
      taxName: "",
      description: "",
      quantity: 1,
      unit_price: 0,
      unit_type: "each",
      sort_order: invoiceData.invoice_items.length + 1,
    };

    setInvoiceData((prev) => ({
      ...prev,
      invoice_items: [...invoiceData.invoice_items, newItem],
    }));
  };

  const removeInvoiceItem = (index: number) => {
    const updatedItems = [...invoiceData.invoice_items];
    updatedItems.splice(index, 1);

    const reorderedItems = updatedItems.map((item, idx) => ({
      ...item,
      sort_order: idx + 1,
    }));
    setItemErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    setInvoiceData((prev) => ({
      ...prev,
      invoice_items: reorderedItems,
    }));
    setGroupItems([]);
  };

  const updateInvoiceItem = (index: number, field: string, value: any) => {
    setInvoiceData((prevInvoiceData) => {
      const updatedItems = [...prevInvoiceData.invoice_items];

      if (field === "sac_gst_rate") {
        const selectedOption = gstRateOptions.find(
          (opt) => Number(opt.value) === Number(value)
        );
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: value,
          ["taxName"]: selectedOption?.label || "",
        };
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: value,
        };
      }

      return {
        ...prevInvoiceData,
        invoice_items: updatedItems,
      };
    });
  };

  const handleFileUpload = async (event: FileUploadHandlerEvent) => {
    const files = event.files;
    setFileUploadError("");

    const validationResults = files.map((file) => ({
      file,
      validation: validateFile(file),
    }));

    const invalidFiles = validationResults.filter(
      (result) => !result.validation.isValid
    );

    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles
        .map((result) => result.validation.error)
        .join("\n");
      setFileUploadError(errorMessages);
      event.options.clear();
      return;
    }

    try {
      const validFiles = validationResults.map((result) => result.file);

      const uploadedDocuments = await Promise.all(
        validFiles.map(async (file: File) => {
          const base64Content = await fileToBase64(file);

          const document: InvoiceDocumentDto = {
            file_path: "",
            file_name: file.name,
            file_type: file.type,
            file_size: file.size,
            upload_date: new Date().toISOString(),
            base64Content: base64Content,
          };

          return document;
        })
      );

      setInvoiceData((prev) => ({
        ...prev,
        documents: [...(prev.documents || []), ...uploadedDocuments],
      }));

      event.options.clear();
    } catch (error) {
      console.error("Error processing files:", error);
      setFileUploadError(
        localization?.FileUploadError ||
        "An error occurred during file upload. Please try again."
      );
      event.options.clear();
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Content = base64String.split(",")[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const removeDocument = (index: number) => {
    const updatedDocuments = [...(invoiceData.documents || [])];
    updatedDocuments.splice(index, 1);

    setInvoiceData((prev) => ({
      ...prev,
      documents: updatedDocuments,
    }));
  };

  const downloadDocument = (doc: InvoiceDocumentDto, index: number) => {
    try {
      if (!doc.base64Content) {
        dispatch(
          showToast({
            detail:
              localization?.DocumentNotAvailable ||
              "Document content not available for download",
            severity: "error",
            summary: localization?.Error || "Error",
          })
        );
        return;
      }

      const byteCharacters = atob(doc.base64Content);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: doc.file_type || "application/octet-stream",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || `document_${index + 1}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      dispatch(
        showToast({
          detail: `${doc.file_name} ${localization?.FileUploadedSuccessfully || "downloaded successfully"
            }`,
          severity: "success",
          summary: localization?.Success || "Success",
        })
      );
    } catch (error) {
      console.error("Error downloading document:", error);
      dispatch(
        showToast({
          detail:
            localization?.FailedToDownloadDoc || "Failed to download document",
          severity: "error",
          summary: localization?.Error || "Error",
        })
      );
    }
  };

  const checkIfSACSelected = () => {
    const errors: { [key: number]: boolean } = {};
    invoiceData.invoice_items.forEach((item, idx) => {
      if (
        item.sac_gst_rate === undefined ||
        item.sac_gst_rate === null ||
        item.sac_gst_rate < 0 ||
        !item.sacCode
      ) {
        errors[idx] = true;
      }
    });

    setValidationErrors(errors);

    const hasError = Object.values(errors).some(Boolean);
    if (hasError) {
      dispatch(
        showToast({
          detail:
            localization?.SelectSACForAllItems ||
            "Please select SAC for all items",
          severity: "error",
          summary: localization?.Error || "Error",
        })
      );
      return false;
    }
    return true;
  };

  const handleSubmit = (
    e: React.FormEvent,
    action: InvoiceStatusEnum,
    skipNavigate: boolean = false
  ) => {
    e.preventDefault();
    if (!checkIfSACSelected()) {
      return;
    }

    if (isDuplicateNumber) {
      dispatch(
        showToast({
          detail:
            localization?.DuplicateInvoiceNumber ||
            "This invoice number already exists. Please use a different number.",
          severity: "error",
          summary: localization?.Error || "Error",
        })
      );
      return;
    }

    if (total <= 0) {
      dispatch(
        showToast({
          detail:
            localization?.TotalAmountZero ||
            "Total amount cannot be zero. Please add amount to the invoice.",
          severity: "error",
          summary: localization?.Error || "Error",
        })
      );
      return;
    }

    const updatedInvoiceData = {
      ...invoiceData,
      status:
        action === InvoiceStatusEnum.DRAFT
          ? InvoiceStatusEnum.DRAFT
          : InvoiceStatusEnum.PENDING,
      gstType: selectedTaxType,
      gstRate: selectedTaxRate?.gstRate
        ? parseFloat(selectedTaxRate?.gstRate)
        : 0,
      natureOfPayment: selectedTaxRate?.nature_of_payment,
      threashold: selectedTaxRate?.threshold,
      yearOfGst: selectedTaxRate?.year,
      tax_total: taxTotal,
      emailTo: emailTo
        .split(",")
        .map((email) => email.trim()) // Trim spaces
        .filter((email) => email !== ""),
      emailCc: emailCc
        .split(",")
        .map((email) => email.trim()) // Trim spaces
        .filter((email) => email !== ""),
      emailSubject: emailSubject,
      emailMessage: emailMessage,
      invoiceNumber: invoiceNumber, // Ensure invoice number is included
      // round_off: roundoffValue
    };

    onSubmit(updatedInvoiceData, skipNavigate);
  };

  const paymentSettingChecker = async () => {
    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) return;
    const isSettingsExist = await getTenantPaymentSettingsByTenantId(
      dispatch,
      tenantId
    );
    if (isSettingsExist) {
      setWithPaymentLink(true);
    } else {
      setWithPaymentLink(false);
    }
  };



  const handleSendInvoice = async (
    propsEmailTo: string,
    propsEmailCc: string,
    template_id?: number
  ) => {
    if (!invoiceData.clientName) {
      return;
    }
    setEmailCc(propsEmailCc);
    setEmailTo(propsEmailTo);

    if (total <= 0) {
      dispatch(
        showToast({
          detail:
            localization?.TotalAmountZero ||
            "Total amount cannot be zero. Please add amount to the invoice.",
          severity: "error",
          summary: localization?.Error || "Error",
        })
      );
      return;
    }

    const updatedInvoiceData = {
      ...invoiceData,
      status: InvoiceStatusEnum.PENDING,
      gstType: selectedTaxType,
      gstRate: selectedTaxRate?.gstRate,
      natureOfPayment: selectedTaxRate?.nature_of_payment,
      threashold: selectedTaxRate?.threshold,
      yearOfGst: selectedTaxRate?.year,
      emailTo: propsEmailTo
        .split(",")
        .map((email) => email.trim()) // Trim spaces
        .filter((email) => email !== ""),
      emailCc: propsEmailCc
        .split(",")
        .map((email) => email.trim()) // Trim spaces
        .filter((email) => email !== ""),
      emailSubject: emailSubject,
      emailMessage: emailMessage,
      withPaymentLink,
      choosedTemplateId: template_id || invoiceData.template_id,
    };

    onSubmit(updatedInvoiceData);
  };

  const openSendDialog = (e: any) => {
    e.preventDefault();
    if (!checkIfSACSelected()) {
      return;
    }
    setEmailSubject(
      localization?.InvoiceFromYourCompany || `Invoice from Your Company`
    );
    setEmailMessage(
      `${localization?.PleaseAttachedInvoice || "Dear"} ${invoiceData.clientName
      },\n\n${localization?.PleaseAttachedInvoice ||
      "Please find attached your invoice for"
      } ${invoiceData.currency_code} ${total}.\n\n${localization?.PaymentIsDueBy || "Payment is due by"
      } ${new Date(invoiceData.due_date).toLocaleDateString()}.\n\n${localization?.ThankYouForYourBusiness || "Thank you for your business."
      }`
    );

    setSendDialogVisible(true);
  };



  const onTextChange = (e: any, options: any) => {
    const inputValue = e.target.value;
    setValue(inputValue);

    updateInvoiceItem(options.rowIndex, "item_name", inputValue);

    const searchText = inputValue.toLowerCase();

    const matches: any = items.filter((opt) =>
      opt.item_name?.toLowerCase().includes(searchText)
    );

    setFilteredOptions(matches);
    if (matches.length > 0) {
      overlayRef.current.show(e);

      // Update only item_name if there are matches
      updateInvoiceItem(options.rowIndex, "item_name", inputValue);
    } else {
      overlayRef.current.hide();

      // Replace the row with a new item structure
      const newItem: any = {
        isNew: true,
        item_name: inputValue, // set typed value here
        item_code: null,
        sacCode: null,
        sac_gst_rate: 0,
        description: "",
        quantity: 1,
        unit_price: 0,
        unit_type: "each",
        sort_order: options.rowIndex + 1, // or invoiceData.invoice_items.length + 1
      };
      setInvoiceData((prevInvoiceData) => {
        const updatedItems = [...prevInvoiceData.invoice_items];

        updatedItems[options.rowIndex] = {
          ...newItem,
          item_name: inputValue,
        };

        return {
          ...prevInvoiceData,
          invoice_items: updatedItems,
        };
      });
    }
  };

  const onOptionSelect = (e: any, options: any) => {
    const selectedLabel = e.value;
    const updatedItems: any = [...invoiceData.invoice_items];

    updatedItems[options.rowIndex] = {
      ...updatedItems[options.rowIndex],
      item_code: selectedLabel.item_code,
      item_id: selectedLabel.item_id,
      item_name: selectedLabel?.item_name,
      description: selectedLabel.description || selectedItem?.item_name,
      unit_price: parseFloat(selectedLabel.unit_price) || 0,
      unit_type: selectedLabel.unit_type || "each",
      sacCode: selectedLabel.sacCode,
      sac_gst_rate: selectedLabel.sac_gst_rate,
      quantity: updatedItems[options.rowIndex].quantity || 1,
      sort_order:
        updatedItems[options.rowIndex].sort_order || options.rowIndex + 1,
      isNew: false,
    };
    if (updatedItems[options.rowIndex]?.sacCode)
      setItemErrors((prev) => {
        const updated = { ...prev };
        delete updated[options.rowIndex];
        return updated;
      });


    setInvoiceData((prev) => ({
      ...prev,
      invoice_items: updatedItems,
    }));
    setValue(
      (prev) => prev + (prev.trim() ? " " : "") + selectedLabel.item_name
    );
    overlayRef.current.hide();
  };

  const handleOpenOverlay = (event: React.MouseEvent) => {
    searchBoxRef.current?.toggle(event);
  };


  const sacDetailsSearch = async () => {
    const sacDetails = await getAllSACDetails(dispatch, { search: searchTerm });
    setAllSACDetails(sacDetails?.rows);
  };

  const setSavedSAC = (e: any, rowIndex: any) => {
    const selectedSAC = e.value;

    const updatedInvoiceItems: any = [...invoiceData.invoice_items];
    updatedInvoiceItems[rowIndex] = {
      ...updatedInvoiceItems[rowIndex],
      sacCode: selectedSAC.SACCode,
      // sacGstRate: selectedSAC.gstRate,
    };
    setInvoiceData((prev) => ({
      ...prev,
      invoice_items: updatedInvoiceItems,
    }));
  };

  const fetchItems = async () => {
    try {
      const response = await getAllItems(dispatch, {
        Page: 1,
        Limit: 100,
        SortBy: "ASC",
        SortProperty: "item_name",
      });

      if (response && response.List) {
        setItems(response.List);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      const paymentTermsResponse = await getAllPaymentTerms(dispatch);
      if (paymentTermsResponse) {
        setPaymentTerms(
          paymentTermsResponse.map((term: any) => ({
            label: term.name,
            value: term.payment_term_id,
          }))
        );
      }

      const businessTypesResponse = await getAllBusinessTypes(dispatch);
      if (businessTypesResponse) {
        setBusinessTypes(
          businessTypesResponse.map((type: any) => ({
            label: type.name,
            value: type.business_type_id,
            description: type.description,
          }))
        );
      }
      if (initialData.business_type) {
        const preSelectedBusinessType = businessTypesResponse.find(
          (typ) => typ.name === initialData.business_type
        );
        if (preSelectedBusinessType) {
          setInvoiceData((prev) => ({
            ...prev,
            business_type_id: preSelectedBusinessType?.business_type_id,
          }));
          setSelectedBusinessTypeData({
            label: preSelectedBusinessType?.name,
            value: preSelectedBusinessType?.business_type_id,
            description: preSelectedBusinessType?.description,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  //#endregion


  //#region all Templates

  const valueTemplate = (option: any, props: any) => {
    if (!option || !option.label) {
      return <span className="text-gray-400">{props.placeholder}</span>;
    }

    return (
      <div className="flex flex-col">
        <span className="font-semibold text-sm text-black">{option.label}</span>
        <div className="flex text-xs text-gray-600 space-x-3">
          <span
            className={`font-bold px-2 py-0.5 rounded ${option.rate === 0.1
              ? "bg-green-100 text-green-600 border-green-300"
              : "bg-orange-100 text-orange-500 border border-orange-300"
              }`}
          >
            {option?.rate?.toFixed(1)}%
          </span>

          <span className="mr-2">
            {localization?.Threshold || "Threshold"}: $
            {option.threshold.toLocaleString()}
          </span>
          <span>{option.year}</span>
        </div>
      </div>
    );
  };

  const documentItemTemplate = (file: any, props: any) => {
    return (
      <div>
        <div className="flex items-center justify-between p-3 hover:bg-gray-100 rounded-md transition-colors">
          <div className="flex items-center flex-grow">
            <FontAwesomeIcon
              icon={faFile}
              className="mr-3 text-blue-500 text-lg"
            />
            <div className="flex flex-col">
              <span className="font-medium text-gray-800">
                {file.file_name}
              </span>
              {file.file_size && (
                <span className="text-sm text-gray-500">
                  {(file.file_size / 1024).toFixed(2)} KB
                </span>
              )}
              {file.upload_date && (
                <span className="text-xs text-gray-400">
                  {localization?.UploadedDocuments || "Uploaded"}:{" "}
                  {new Date(file.upload_date).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-4">
            <Button
              type="button"
              icon="pi pi-download"
              className="p-button-text p-button-rounded p-button-sm hover:bg-blue-100"
              onClick={(e: any) => {
                e.preventDefault();
                e.stopPropagation();
                downloadDocument(file, props.index);
              }}
              aria-label={`${localization?.Download || "Download"} ${file.file_name
                }`}
              children={<FontAwesomeIcon icon={faDownload} />}
            />

            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded p-button-sm hover:bg-red-100 text-red-600"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                removeDocument(props.index);
              }}
              aria-label={`${localization?.Remove || "Remove"} ${file.file_name
                }`}
              children={<FontAwesomeIcon icon={faTrash} />}
            />
          </div>
        </div>
      </div>
    );
  };

  const actionBodyTemplate = (data: any, props: any) => {
    return (
      <Button
        className="actionBtn p-button-rounded p-button-danger border-red-500 hover:bg-red-500 hover:text-white p-button-text"
        onClick={() => removeInvoiceItem(props.rowIndex)}
        children={<FontAwesomeIcon icon={faTrash} />}
      />
    );
  };

  const quantityEditor = (options: any) => {
    const rowIndex = options.rowIndex;
    const rowItem: any = invoiceData.invoice_items[rowIndex];
    return (
      <InputNumber
        value={rowItem.quantity || ""}
        onValueChange={(e) =>
          updateInvoiceItem(options.rowIndex, "quantity", e.value)
        }
        min={1}
        className="w-full"
      />
    );
  };

  const descriptionEditor = (options: any) => {
    const rowIndex = options.rowIndex;
    const rowItem: any = invoiceData.invoice_items[rowIndex];
    return (
      <InputText
        value={rowItem.description || ""}
        onChange={(e) =>
          updateInvoiceItem(options.rowIndex, "description", e.target.value)
        }
        className="w-full"
      />
    );
  };
  const itemEditor = (options: any) => {
    const rowIndex = options.rowIndex;
    const rowItem: any = invoiceData.invoice_items[rowIndex];
    const serViceName = allSACDetails.find(
      (detail) => detail.SACCode === rowItem.sacCode
    )?.ServiceName;
    const hasError = itemErrors[rowIndex];
    return (
      <div>
        <InputTextarea
          className={`border rounded-md p-2 w-full ${hasError
            ? '!border-red-500 !bg-red-50'
            : 'border-gray-300'
            }`}
          value={rowItem.item_name || ""}
          onChange={(e: any) => onTextChange(e, options)}
          rows={5}
          cols={30}
          placeholder={localization?.TypeSomething || "Type something..."}
          onBlur={(e) => {
            // Check if the blur event is caused by clicking the SAC button
            const relatedTarget = e.relatedTarget as HTMLElement;
            const isClickingSACButton = relatedTarget?.closest?.('.sac-button-container, [data-sac-button="true"]');

            // Only validate if the blur is NOT caused by clicking the SAC button
            if (!isClickingSACButton && rowItem.item_name && !rowItem.sacCode) {
              setItemErrors((prev) => ({
                ...prev,
                [rowIndex]: localization?.PleaseSelectSAC || "Please select a SAC code",
              }));
            }
          }}
        />
        {hasError && (
          <small className="text-red-600 font-medium leading-5 block mt-1">
            ⚠️ {itemErrors[rowIndex]}
          </small>
        )}

        {/* {hasSacFeature && ( */}
        <div
          className="flex items-center gap-2 sac-button-container"
          data-sac-button="true"
        // className="flex items-center gap-2"
        >
          <Tag
            value={localization?.Service || "SERVICE"}
            severity="success"
          />
          <span>SAC:</span>
          <span>{rowItem.sacCode}</span>
          <Button
            icon="pi pi-pencil"
            className="p-0 m-0 text-white"
            onClick={(e) => {
              e.preventDefault();
              sacDetailsSearch();
              setAllSACDetails([]);
              setSearchTerm(null);
              handleOpenOverlay(e);
            }}
          >
            <FontAwesomeIcon icon={faPencil} />{" "}
            {localization?.Choose || "Choose"}
          </Button>
        </div>
        {/* // )} */}
        <OverlayPanel ref={searchBoxRef}>
          <div className="flex flex-col gap-2 p-2 w-[300px]">
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                localization?.SearchSACOrService ||
                "Search SAC or Service Name..."
              }
              className="w-full"
            />
            {allSACDetails.length > 0 && (
              <ListBox
                options={allSACDetails}
                optionLabel="SACCode"
                itemTemplate={(option) => (
                  <div className="flex flex-col">
                    <div className="font-semibold">SAC: {option.SACCode}</div>
                    <div className="text-sm text-gray-700">
                      {option.ServiceName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {localization?.GSTRate || "GST Rate"}: {option.gstRate}%
                    </div>
                  </div>
                )}
                onChange={(e: any) => {
                  setSavedSAC(e, options.rowIndex);
                  searchBoxRef.current?.hide();
                  // clear error
                  setItemErrors((prev) => {
                    const updated = { ...prev };
                    delete updated[rowIndex];
                    return updated;
                  });
                }}
                style={{ width: "300px" }}
                listStyle={{ maxHeight: "200px" }}
              />
            )}
          </div>
        </OverlayPanel>
        <OverlayPanel ref={overlayRef}>
          <ListBox
            options={filteredOptions}
            optionLabel="item_name"
            onChange={(e: any) => onOptionSelect(e, options)}
            style={{ width: "200px" }}
          />
        </OverlayPanel>
      </div>
    );
  };



  const sacGstEditor = (options: any) => {
    const rowIndex = options.rowIndex;
    const rowItem: any = invoiceData.invoice_items[rowIndex];

    const validateNewTax = () => {
      let taxNameError = false;
      let taxRateError = false;
      let taxTypeError = false;

      let isValid = true;

      if (!newTaxData.name) {
        taxNameError = true;
        isValid = false;
      }

      const rate = parseFloat(newTaxData.rate);
      if (!newTaxData.rate || isNaN(rate) || rate < 0) {
        taxRateError = true;
        isValid = false;
      }

      if (!newTaxData.type && !digitalServices) {
        taxTypeError = true;
        isValid = false;
      }

      setNewTaxErrors({
        taxName: taxNameError,
        taxRate: taxRateError,
        taxType: taxTypeError,
      });

      return isValid;
    };

    const handleSaveNewTax = async () => {
      if (!validateNewTax()) return;

      const isSameExist = await isSameGSTExist();

      if (!isSameExist) {
        return;
      }

      const rate = parseFloat(newTaxData.rate);

      if (!invoiceData.business_type_id) {
        return dispatch(
          showToast({
            severity: "warn",
            summary: "Warning",
            detail: "Select GST Treatment first",
            life: 3000,
          })
        );
      }
      // if (invoiceData.business_type_id === 1 && !digitalServices) {
      //   return dispatch(
      //     showToast({
      //       severity: "warn",
      //       summary: "Warning",
      //       detail: "Please,check the checkbox to proceed",
      //       life: 3000,
      //     })
      //   );
      // }

      const createGSTInput: CreateGSTRate = {
        gstTreatmentId: !digitalServices ? invoiceData.business_type_id : 1,
        gstRateName: newTaxData.name,
        gstNameVal: rate,
        tax_type: !digitalServices ? newTaxData.type : null,
      };

      const createdGst = await createGSTRatesInGSTTreatment(
        dispatch,
        createGSTInput
      );
      if (createdGst) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Success",
            detail: createdGst,
            life: 3000,
          })
        );
      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: "Error",
            detail: "Unable to create GST rate",
            life: 3000,
          })
        );
      }

      // setGstRateOptions(prev => [...prev, newTaxOption]);
      await getAllGstRateOptions();
      setShowNewTaxDialog(false);
      setNewTaxData({ name: "", rate: "", type: "" });
      setDigitalServices(false);
    };

    return (
      <>
        <Dropdown
          value={rowItem.sac_gst_rate?.toString() ?? ""}
          optionLabel="label"
          optionValue="value"
          options={gstRateOptions}
          // editable
          placeholder={localization?.SelectGSTRate || "Select GST Rate"}
          className="w-full"
          onChange={(e) => {
            // Allow user to type freely without interfering
            const rawValue = e.target?.value ?? e.value;
            updateInvoiceItem(rowIndex, "sac_gst_rate", e.value);
          }}
          // onBlur={(e) => {
          //   // Step 1: Extract the raw input safely
          //   const rawInput =
          //     e?.target?.value ??
          //     e?.target?.innerText ??
          //     rowItem.sacGstRate?.toString() ??
          //     "";
          //   // Step 2: Extract numeric portion from label or manual input (e.g., "IGST (6%)")
          //   const match = rawInput.match(/-?\d+(\.\d+)?/); // ? updated regex
          //   const cleaned = match ? match[0] : ""; // cleaned is a valid number string or ""

          //   // Step 3: Handle empty input
          //   if (cleaned === "") {
          //     updateInvoiceItem(rowIndex, "sacGstRate", 0);
          //     if (!rowItem.sacCode) {
          //       updateInvoiceItem(rowIndex, "sacCode", null);
          //     }
          //     return;
          //   }

          //   const numeric = parseFloat(cleaned);

          //   // Step 4: Handle invalid numbers (NaN)
          //   if (isNaN(numeric)) {
          //     updateInvoiceItem(rowIndex, "sacGstRate", 0);
          //     if (!rowItem.sacCode) {
          //       updateInvoiceItem(rowIndex, "sacCode", null);
          //     }
          //     return;
          //   }

          //   // Step 5: Handle negative input ? call warning + reset to 0
          //   if (numeric < 0) {
          //     handleInvalidValue(); // will show toast and reset to 0 internally
          //     return; // ? Make sure we STOP here and do not apply negative value
          //   }

          //   // Step 6: Valid value ? update it
          //   updateInvoiceItem(rowIndex, "sacGstRate", numeric);
          //   if (!rowItem.sacCode) {
          //     updateInvoiceItem(rowIndex, "sacCode", null);
          //   }
          // }}
          panelFooterTemplate={() =>
            invoiceData.business_type_id === 1 ? (
              <div
                className="p-3 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => setShowNewTaxDialog(true)}
              >
                <div className="flex items-center text-blue-600 font-medium">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  New Tax
                </div>
              </div>
            ) : (
              <></>
            )
          }
        />
        {/* New Tax Dialog */}
        <Dialog
          header="New Tax"
          visible={showNewTaxDialog}
          onHide={() => {
            setShowNewTaxDialog(false);
            setNewTaxData({ name: "", rate: "", type: "" });
            setDigitalServices(false);
          }}
          style={{ width: "450px" }}
          modal
          className="p-6"
        >
          <div className="space-y-4">
            <div>
              <label
                htmlFor="tax-name"
                className="block text-sm font-medium mb-1"
              >
                Tax Name*
              </label>
              <InputText
                id="tax-name"
                value={newTaxData.name}
                onChange={(e) => {
                  setNewTaxData((prev) => ({ ...prev, name: e.target.value }));
                  setNewTaxErrors({ ...newTaxErrors, taxName: false });
                }}
                placeholder="Enter tax name"
                className="w-full"
              />
              {newTaxErrors.taxName && (
                <span
                  className={`w-full ${newTaxErrors.taxName
                    ? "text-red-500 text-sm block mt-1"
                    : ""
                    }`}
                >
                  Tax name is required
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="tax-rate"
                className="block text-sm font-medium mb-1"
              >
                Rate (%)*
              </label>
              <div className="flex">
                <InputText
                  id="tax-rate"
                  value={newTaxData.rate}
                  onChange={(e) => {
                    setNewTaxData((prev) => ({
                      ...prev,
                      rate: e.target.value,
                    }));
                    setNewTaxErrors({ ...newTaxErrors, taxRate: false });
                  }}
                  placeholder="Enter rate"
                  className="flex-1 rounded-r-none"
                />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-600">
                  %
                </span>
              </div>
              {newTaxErrors.taxRate && (
                <span
                  className={`w-full ${newTaxErrors.taxRate
                    ? "text-red-500 text-sm block mt-1"
                    : ""
                    }`}
                >
                  Tax rate is required
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="tax-type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Tax Type
              </label>
              <Dropdown
                value={newTaxData.type}
                onChange={(e) => {
                  setNewTaxData((prev) => ({ ...prev, type: e.value }));
                  setNewTaxErrors({ ...newTaxErrors, taxType: false });
                }}
                options={allTaxTypes}
                optionLabel="name"
                optionValue="code"
                placeholder="Select a Tax Type"
                className="w-full md:w-14rem"
                disabled={digitalServices}
              />

              {newTaxErrors.taxType && (
                <span
                  className={`w-full ${newTaxErrors.taxType
                    ? "text-red-500 text-sm block mt-1"
                    : ""
                    }`}
                >
                  Tax type is required
                </span>
              )}
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="digital-services"
                checked={digitalServices}
                onChange={(e) => setDigitalServices(e.target.checked || false)}
                className="mt-1"
              />
              <label
                htmlFor="digital-services"
                className="text-sm text-gray-700"
              >
                This tax will be applicable only during the sale of digital
                services to overseas customers.
              </label>
            </div>

            {digitalServices && (
              <div>
                <label
                  htmlFor="country"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Select Country
                </label>
                <Dropdown
                  id="country"
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.value)}
                  options={countryList}
                  optionLabel="name"
                  optionValue="code"
                  placeholder="Select a country"
                  className="w-full"
                  filter
                  showClear
                />
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                onClick={() => {
                  setShowNewTaxDialog(false);
                  setNewTaxData({ name: "", rate: "", type: "" });
                  setDigitalServices(false);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveNewTax}>Save</Button>
            </div>
          </div>
        </Dialog>
      </>
    );
  };

  const priceEditor = (options: any) => {
    const rowIndex = options.rowIndex;
    const rowItem: any = invoiceData.invoice_items[rowIndex];

    return (
      <InputNumber
        value={rowItem.unit_price || 0}
        onValueChange={(e) => {
          updateInvoiceItem(options.rowIndex, "unit_price", e.value);
        }}
        mode="currency"
        currency={invoiceData?.currency_code}
        locale="en-US"
        minFractionDigits={2}
        className="w-full"
      />
    );
  };

  const discountEditor = (options: any) => {
    const rowIndex = options.rowIndex;
    const rowItem: any = invoiceData.invoice_items[rowIndex];

    return (
      <InputNumber
        value={rowItem.discount || 0}
        onValueChange={(e) => {
          const newValue = e.value || 0;
          const updatedItems = [...invoiceData.invoice_items];
          updatedItems[rowIndex] = {
            ...updatedItems[rowIndex],
            discount: newValue,
          };
          setInvoiceData((prev) => ({
            ...prev,
            invoice_items: updatedItems,
          }));
        }}
        mode="decimal"
        min={0}
        max={100}
        suffix="%"
        className="w-full"
      />
    );
  };

  //#endregion
  //#region useEffects

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== null) {
        sacDetailsSearch();
      }
    }, 2000);
    sacDetailsSearch();
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    if (invoiceData?.invoice_items?.length > 0) {
      const grouped = calculateGstLabelsAndTax(
        invoiceData.invoice_items,
        gstRateOptions
      );

      setGroupItems(grouped);
    }
  }, [invoiceData.invoice_items, gstRateOptions]);

  useEffect(() => {
    getTdsOrTcsDetails();
    setTaxTotal(0);
    setTotal(0);
    setSelectedTaxRate(null);
  }, [selectedTaxType]);


  useEffect(() => {
    calculateTotals();
  }, [
    invoiceData.invoice_items,
    invoiceData.tax_inclusive,
    taxRates,
    selectedTaxRate,
    showRoundoff,
    invoiceData.round_off,
    // invoiceData,
  ]);

  useEffect(() => {
    fetchItems();
    fetchCurrencies();
    fetchInitialData();
    paymentSettingChecker();
  }, []);

  useEffect(() => {
    if (invoiceData.issue_date && invoiceData.payment_terms) {
      setInvoiceData((prev) => ({
        ...prev,
        due_date: initialData.due_date
          ? new Date(initialData.due_date)
          : getDueDateByPaymentTerm(
            invoiceData.payment_terms ?? 0,
            invoiceData.issue_date
          ),
      }));
    }
  }, [invoiceData.issue_date]);

  useEffect(() => {
    if (preselectedClient) {
      setSelectedClient(preselectedClient);
      handleClientChange({ value: preselectedClient, originalEvent: {} });
    }
  }, [preselectedClient]);

  // useEffect(() => {
  //   const fetchDefaultInvoiceNumber = async () => {
  //     if (mode === "create" && !initialData.invoiceNumber) {
  //       const response = await getDefaultSequence(dispatch);
  //       if (response) {
  //         const previewNumber = String(response.next_number).padStart(
  //           response.number_padding,
  //           "0"
  //         );
  //         const suffix = Number(response.suffix || 0) + Number(response.next_number);
  //         const previewFormat = `${response.prefix}${previewNumber}${suffix}`;
  //         setInvoiceNumber(previewFormat);
  //         setInvoiceData((prev) => ({
  //           ...prev,
  //           invoiceNumber: previewFormat,
  //         }));
  //       }
  //     }
  //   };

  //   fetchDefaultInvoiceNumber();
  // }, [initialData.invoiceNumber]);

  useEffect(() => {
    const fetchDefaultInvoiceNumber = async () => {
      if (mode === "create" && !initialData.invoiceNumber) {
        const response = await getDefaultSequence(dispatch);
        if (response) {
          const previewNumber = String(response.next_number).padStart(
            response.number_padding,
            "0"
          );

          const previewFormat = `${response.prefix}${previewNumber}${response.suffix}`;
          setInvoiceNumber(previewFormat);
          setInvoiceData((prev) => ({
            ...prev,
            invoiceNumber: previewFormat,
          }));
        }
      }
    };

    fetchDefaultInvoiceNumber();
  }, [initialData.invoiceNumber]);

  useEffect(() => {
    getAllGstRateOptions();
  }, [selectedBusinessTypeData]);

  //#endregion
  return (
    <>
      <Toast ref={toast} />
      <form>
        <Card className="mb-4 card_content_padding">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="client"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.Client_Required || "Client*"}
              </label>
              <ClientSelector
                value={selectedClient}
                onChange={handleClientChange}
                preselectedClient={preselectedClient}
                invoiceData={invoiceData}
                setInvoiceData={setInvoiceData}
                className="w-full"
              />
            </div>

            {/* issue date */}
            <div>
              <label
                htmlFor="issue_date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.IssueDate || "Issue Date*"}
              </label>
              <Calendar
                id="issue_date"
                value={invoiceData.issue_date}
                onChange={(e) => handleDateChange(e, "issue_date")}
                showIcon
                dateFormat="dd/mm/yy"
                className="w-full"
              />
            </div>

            {/* payment terms  */}
            <div>
              <label
                htmlFor="payment_terms"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.PaymentTerms || "Payment Terms*"}
              </label>
              <Dropdown
                id="payment_terms"
                name="payment_terms"
                value={
                  invoiceData.payment_term_id
                    ? invoiceData.payment_term_id
                    : parseInt(initialData.payment_term_id)
                }
                options={paymentTerms}
                onChange={(e: any) => handleDropdownChange(e, "payment_terms")}
                optionLabel="label"
                optionValue="value"
                placeholder={
                  localization?.SelectPaymentTerms || "Select Payment Terms"
                }
                className="w-full"
              />
            </div>

            {/* due date  */}
            <div>
              <label
                htmlFor="due_date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.DueDate_Required || "Due Date*"}
              </label>
              <Calendar
                id="due_date"
                value={invoiceData.due_date}
                onChange={(e) => handleDateChange(e, "due_date")}
                showIcon
                dateFormat="dd/mm/yy"
                className="w-full"
              />
            </div>

            {/* invoice number */}
            <div>
              <label
                htmlFor="invoiceNumber"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.InvoiceNumber_Required || "Invoice Number*"}
              </label>
              <div className="p-inputgroup">
                <InputText
                  id="invoiceNumber"
                  value={invoiceNumber}
                  onChange={handleInvoiceNumberChange}
                  onBlur={() => checkDuplicateInvoiceNumber(invoiceNumber)}
                  className={`w-full ${isDuplicateNumber ? "p-invalid" : ""}`}
                  placeholder={
                    localization?.EnterInvoiceNumber || "Enter invoice number"
                  }
                />
                {isCheckingDuplicate && (
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-spin pi-spinner"></i>
                  </span>
                )}
              </div>
              {isDuplicateNumber && (
                <small className="p-error">
                  {localization?.DuplicateInvoiceNumber ||
                    "This invoice number already exists"}
                </small>
              )}
            </div>

            {/* currency  */}
            <div>
              <label
                htmlFor="currency"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.Currency || "Currency*"}
              </label>
              <CurrencySelector
                value={
                  invoiceData.currency_code
                    ? invoiceData.currency_code
                    : initialData.currency_code
                }
                onChange={(e) => handleDropdownChange(e, "currency")}
                loading={loading}
                currencies={currencies}
                setCurrencies={setCurrencies}
                className="w-full"
              />
            </div>

            {/* industry */}
            <div>
              <label
                htmlFor="industry_id"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.Industry || "Industry"}
              </label>
              <IndustrySelector
                value={
                  invoiceData.industry_id
                    ? invoiceData.industry_id
                    : initialData.industry_id
                }
                onChange={(e: any) => handleDropdownChange(e, "industry_id")}
                className="w-full"
              />
            </div>

            {/* overseas  */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {localization?.BusinessType || "GST Treatment *"}
              </label>
              <Dropdown
                // value={invoiceData.business_type_id}
                value={invoiceData.business_type_id
                  ? invoiceData.business_type_id
                  : initialData.business_type_id}
                options={businessTypes}
                onChange={(e) => handleDropdownChange(e, "business_type")}
                placeholder={
                  localization?.SelectBusinessType || "Select GST Treatment"
                }
                itemTemplate={(option: any) => (
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm">
                      {option.label}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-normal break-words w-full">
                      {option.description}
                    </span>
                  </div>
                )}
                valueTemplate={(option: any) =>
                  option ? (
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">
                        {option.label}
                      </span>
                      <span className="text-xs text-gray-500 whitespace-normal break-words w-full">
                        {option.description}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400">
                      {localization?.SelectBusinessType ||
                        "Select GST Treatment"}
                    </span>
                  )
                }
                className="w-full"
                panelClassName="w-[250px]" // ?? controls the dropdown list width
              />
              {/* Tax Configuration Modal */}
              {/* Tax Configuration Modal */}
              {showTaxConfig && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-xl w-96 max-w-90vw">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Configure Tax Preferences
                      </h3>
                      <button
                        onClick={handleTaxConfigCancel}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    {/* Modal Body */}
                    <div className="p-4 space-y-4">
                      {/* Business Type Display */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST Treatment
                        </label>
                        <div className="p-3 bg-gray-50 rounded-md border">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-medium text-sm">
                              {selectedBusinessTypeData?.label}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* GSTIN Input */}
                      {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GSTIN *
                        </label>
                        <input
                          type="text"
                          value={gstin}
                          onChange={(e) => setGstin(e.target.value)}
                          placeholder="Enter GSTIN"
                          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div> */}

                      {/* Get Taxpayer Details */}
                      <div>
                        <button
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                          onClick={() =>
                            navigate(`/client/edit/${selectedClient?.id}`)
                          }
                        >
                          Get Taxpayer Details
                        </button>
                      </div>

                      {/* Make Permanent Checkbox */}
                      <div>
                        <label className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={makeGstinPermanent}
                            onChange={(e) =>
                              setMakeGstinPermanent(e.target.checked)
                            }
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">
                              Make it permanent?
                            </span>
                            <p className="text-gray-600 text-xs mt-1">
                              Use these values as the default in all future
                              transactions of this customer.
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Modal Footer */}
                    <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handleTaxConfigCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleTaxConfigSave}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card className="mb-4 card_content_padding">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800 mt-0 mb-0">
              {localization?.InvoiceItems || "Invoice Items"}
            </h2>
            <Button
              icon="pi pi-plus"
              onClick={addInvoiceItem}
              className="bg-gold text-white"
              type="button"
            >
              {localization?.AddItem || "Add Item"}
            </Button>
          </div>

          <DataTable
            value={invoiceData.invoice_items}
            // editMode="cell"
            editMode={showNewTaxDialog ? undefined : "cell"}
            className="p-datatable-sm"
            emptyMessage={
              localization?.NoItemsAddedYet ||
              "No items added yet. Click 'Add Item' to begin."
            }
          >
            <Column
              field="item_id"
              header={localization?.Item || "Item"}
              editor={itemEditor}
              body={(rowData, options) => {
                const rowIndex = options.rowIndex;
                const rowItem: any = invoiceData.invoice_items[rowIndex];
                const itemName = rowItem.item_name;
                const hasError = itemErrors[rowIndex];
                return (
                  <div
                    className={`flex flex-col items-start gap-3 p-4 rounded-lg shadow-md transition-all duration-300 ${hasError
                      ? 'border-2 border-red-500 bg-red-50'
                      : 'border border-gray-300 bg-white hover:border-gray-400 hover:shadow-lg'
                      }`}
                  >
                    <span className="text-gray-900 font-medium text-base leading-tight">
                      {itemName}
                    </span>
                    {rowItem?.sacCode > 0 && itemName?.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Tag
                          value="Service"
                          severity="success"
                          className="text-sm"
                        />
                        <span className="text-sm text-gray-700 font-semibold whitespace-nowrap">
                          SAC: {rowItem.sacCode}
                        </span>
                      </div>
                    )}
                    {hasError && (
                      <small className="text-red-600 font-medium leading-5 block">
                        ⚠️ {itemErrors[rowIndex]}
                      </small>
                    )}
                  </div>
                );
              }}
              style={{ width: "20%" }}
            />
            <Column
              field="description"
              header={localization?.Description || "Description"}
              editor={descriptionEditor}
              style={{ width: "25%" }}
            />
            <Column
              field="quantity"
              header={localization?.Quantity || "Quantity"}
              editor={quantityEditor}
              style={{ width: "10%" }}
            />
            <Column
              field="sac_gst_rate"
              header={localization?.ServiceTax || "Service Tax"}
              editor={sacGstEditor}
              style={{ width: "15%" }}
              body={(rowData) => {
                return rowData.sac_gst_rate + " %";
              }}
            />
            <Column
              field="unit_price"
              header={localization?.UnitPrice || "Unit Price"}
              editor={priceEditor}
              style={{ width: "10%" }}
              body={(rowData) => {
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: invoiceData?.currency_code || "USD",
                }).format(rowData.unit_price || 0);
              }}
            />
            <Column
              field="discount"
              header={localization?.Discount || "Discount"}
              editor={discountEditor}
              style={{ width: "10%" }}
              body={(rowData) => `${rowData.discount || 0}%`}
            />

            <Column
              body={(rowData, options) => {
                const rowIndex = options.rowIndex;
                const rowItem: any = invoiceData.invoice_items[rowIndex];

                const qty = rowData.quantity || 0;
                const price = rowData.unit_price || 0;
                const discount = rowData.discount || 0;

                const totalBeforeDiscount = qty * price;
                const discountedTotal =
                  totalBeforeDiscount - (totalBeforeDiscount * discount) / 100;

                const gstAmount = (discountedTotal * rowItem.sac_gst_rate) / 100;
                const totalWithGST = discountedTotal + gstAmount;

                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: invoiceData.currency_code || "USD",
                }).format(totalWithGST);
              }}
              header={localization?.Total || "Total"}
              style={{ width: "10%" }}
            />
            <Column body={actionBodyTemplate} style={{ width: "5%" }} />
          </DataTable>
        </Card>

        <Card className="mb-4 card_content_padding">
          <h2 className="text-xl font-semibold text-gray-800 mt-0 mb-4">
            {localization?.Attachments || "Attachments"}
          </h2>
          {fileUploadError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm whitespace-pre-line">
                {fileUploadError}
              </p>
            </div>
          )}
          <div className="mb-0">
            <FileUpload
              ref={fileUploadRef}
              name="documents[]"
              multiple
              accept={Object.keys(ALLOWED_FILE_TYPES).join(",")}
              maxFileSize={MAX_FILE_SIZE}
              customUpload={true}
              uploadHandler={handleFileUpload}
              chooseLabel={localization?.SelectFiles || "Select Files"}
              className="p-fileupload-sm P_fileupload"
              auto={true} // This makes it upload immediately on file selection
              headerTemplate={(options: any) => (
                <div className="p-fileupload-buttonbar">
                  <span className=" flex items-center gap-2 p-buttonset">
                    {options.chooseButton}
                    <Button
                      type="button" // ?? important!
                      disabled={invoiceData?.documents?.length === 0}
                      // icon="fa-solid fa-xmark"
                      className="p-3 text-base font-medium flex items-center gap-2"
                      onClick={() => {
                        setInvoiceData((prev) => ({
                          ...prev,
                          documents: [],
                        }));
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="w-4 h-4 text-base mr-2"
                      />
                      {localization?.Cancel || "Cancel"}
                    </Button>
                  </span>
                </div>
              )}
              emptyTemplate={
                (invoiceData.documents?.length ?? 0) > 0 ? (
                  <></>
                ) : (
                  <div className="text-center p-4">
                    <p className="m-0 mb-2">
                      {localization?.DragAndDropFilesHereToUpload ||
                        "Drag and drop files here to upload."}
                    </p>
                    <p className="text-sm text-gray-600 m-0">
                      {localization?.SupportedFormats ||
                        "Supported formats: Excel (.xlsx, .xls, .xlsm, etc.), PDF (.pdf), Word (.docx, .doc, etc.), PowerPoint (.pptx, .ppt, etc.)"}
                    </p>
                    <p className="text-xs text-gray-500 m-0 mt-1">
                      {localization?.MaximumFileSize || "Maximum file size:"}{" "}
                      {MAX_FILE_SIZE / (1024 * 1024)}MB
                    </p>
                  </div>
                )
              }
              onError={() => {
                setFileUploadError(
                  localization?.FileUploadError ||
                  "An error occurred during file upload. Please try again."
                );
              }}
              onClear={() => {
                setFileUploadError("");
              }}
            />
          </div>

          {invoiceData.documents && invoiceData.documents.length > 0 && (
            <div className="border rounded-md p-3 bg-gray-50">
              <h3 className="text-md font-medium text-gray-700 mb-2">
                {localization?.AttachedDocuments || "Attached Documents"} (
                {invoiceData.documents.length})
              </h3>
              <ul className="list-none p-0 m-0">
                {invoiceData.documents.map((doc, index) => (
                  <li
                    key={index}
                    className="border-b border-gray-200 last:border-b-0"
                  >
                    {documentItemTemplate(doc, { index })}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <Card className="mb-4 card_content_padding">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.Notes || "Notes"}
              </label>
              <InputTextarea
                id="notes"
                name="notes"
                value={invoiceData.notes}
                onChange={handleInputChange}
                rows={4}
                className="w-full"
              />
            </div>

            <div>
              <label
                htmlFor="terms_conditions"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {localization?.TermsConditions || "Terms & Conditions"}
              </label>
              <InputTextarea
                id="terms_conditions"
                name="terms_conditions"
                value={invoiceData.terms_conditions}
                onChange={handleInputChange}
                rows={4}
                className="w-full"
              />
            </div>
          </div>
        </Card>
        <Card className="card_content_padding">
          <div className="flex flex-col md:flex-row justify-between">
            <div className="w-full md:w-1/3 mb-6 md:mb-0">
              <div className="mb-0">
                <label className="block font-medium mb-0">
                  {localization?.TaxType || "Tax Type"}
                </label>
                <div className="flex gap-4 my-4">
                  <div className="flex items-center">
                    <RadioButton
                      inputId="TDS"
                      name="taxType"
                      value="TDS"
                      onChange={(e) => setSelectedTaxType(e.value)}
                      checked={selectedTaxType === "TDS"}
                    />
                    <label htmlFor="TDS" className="ml-2">
                      {localization?.TDS || "TDS"}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <RadioButton
                      inputId="TCS"
                      name="taxType"
                      value="TCS"
                      onChange={(e) => {
                        setSelectedTaxType(e.value);
                        setInvoiceData((prev) => ({
                          ...prev,
                          gstRate: 0,
                        }));
                      }}
                      checked={selectedTaxType === "TCS"}
                    />
                    <label htmlFor="TCS" className="ml-2">
                      {localization?.TCS || "TCS"}
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block font-medium mb-2">
                  {localization?.DefaultTaxRate || "Default Tax Rate"}
                </label>

                <Dropdown
                  value={selectedTaxRate}
                  options={taxRates}
                  onChange={(e) => {
                    setSelectedTaxRate(e.value);
                    setInvoiceData((prev) => ({
                      ...prev,
                      gstRate: e.value?.gstRate,
                    }));
                  }}
                  optionLabel="label"
                  itemTemplate={itemTemplate}
                  valueTemplate={valueTemplate}
                  placeholder={
                    localization?.SelectTaxCategory || "Select tax category"
                  }
                  className="w-full shadow-sm rounded-md"
                />
              </div>
              {/* <div>
                <label className="block font-medium m-2">
                  {localization?.DefaultTaxRate || "Total Tax"}
                </label>
                <div>{totalTax?.toFixed(2)}</div>
              </div> */}
            </div>
            {/* Sub Total*/}
            <div className="w-full md:w-1/3">
              <div className="flex justify-between py-2">
                <span className="text-gray-700">
                  {localization?.Subtotal || "Subtotal"}:
                </span>
                <span className="font-medium">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: invoiceData.currency_code || "USD",
                  }).format(subtotal)}
                </span>
              </div>

              {/* discount */}
              <div className="flex justify-between py-2">
                <span className="text-gray-700">
                  {localization?.TotalDiscount || "Total Discount"}:
                </span>
                <span className="font-medium">
                  {totalDiscount > 0 ? "-" : ""}
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: invoiceData.currency_code || "USD",
                  }).format(totalDiscount)}
                </span>
              </div>
              {/* service tax */}
              {/* <div className="flex justify-between py-2">
                <span className="text-gray-700">
                  {localization?.ServiceTax || "Service Tax"}:
                </span>
                <span className="font-medium">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: invoiceData.currency_code || "USD",
                  }).format(sacTotal)}
                </span>
              </div> */}

              <div className="border-b border-gray-200">
                {/* Tax Total Header */}
                <div className="flex items-center justify-between py-3">
                  <span className="font-medium text-gray-700">Tax Summary</span>
                  <span className="font-semibold text-gray-700">
                    {(sacTotal + taxTotal).toFixed(2)}
                  </span>
                </div>

                {/* Tax Breakdown Content - Always Visible */}
                <div className="bg-gray-50 pb-3">
                  <div className="px-4 space-y-2">
                    {/* Service Tax Breakdown */}
                    {groupedItems.length > 0
                      ? groupedItems.map(
                        ({ gstLabel, gstPercentage, taxAmount }, index) => (
                          <div
                            className="flex justify-between py-1 text-sm text-gray-600"
                            key={index}
                          // key={`${gstLabel}-${gstPercentage}`}
                          >
                            <span className="pl-4">
                              {gstLabel
                                ? `${gstLabel} [${gstPercentage}%]`
                                : `${gstPercentage}% [${gstPercentage}%]`}
                            </span>
                            <span>{taxAmount.toFixed(2)}</span>
                          </div>
                        )
                      )
                      : null}
                    {/* TDS/TCS Tax */}
                    {selectedTaxType && (
                      <div className="flex justify-between py-1 text-sm text-gray-600">
                        <span className="pl-4">
                          {localization?.Tax || "Tax(TDS/TCS)"}
                        </span>
                        <span>
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: invoiceData.currency_code || "USD",
                          }).format(taxTotal)}
                        </span>
                      </div>
                    )}
                    {/* No tax breakdown message */}
                    {Object.entries(groupedItems).length === 0 &&
                      taxTotal === 0 &&
                      !selectedTaxType && (
                        <span className="text-sm flex justify-center p-2">
                          No tax breakdown available
                        </span>
                      )}{" "}
                  </div>
                </div>
              </div>

              {/* Checkbox for roundoff */}
              <div className="flex items-center space-x-2 py-2">
                <input
                  type="checkbox"
                  id="roundoff-checkbox"
                  checked={showRoundoff}
                  onChange={(e) => setShowRoundoff(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label
                  htmlFor="roundoff-checkbox"
                  className="text-sm text-gray-700 cursor-pointer select-none"
                >
                  Apply Roundoff
                </label>
              </div>

              {showRoundoff && (
                <div className="flex justify-between py-2">
                  <span className="text-gray-700">
                    {localization?.Roundoff || "Roundoff"}:
                  </span>
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: invoiceData.currency_code || "USD",
                    }).format(invoiceData?.round_off ?? 0)}
                  </span>
                </div>
              )}

              <div className="flex justify-between py-2 border-t border-gray-300 mt-2">
                <span className="text-gray-800 font-semibold">
                  {localization?.TotalColon || "Total"}:
                </span>
                <span className="text-gold font-bold text-xl">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: invoiceData.currency_code || "USD",
                  }).format(total)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6 space-x-2">
            <Button
              className="p-button-outlined"
              onClick={() => window.history.back()}
            >
              {localization?.Cancel || "Cancel"}
            </Button>

            <Button
              onClick={(e: any) => handleSubmit(e, InvoiceStatusEnum.DRAFT)}
              className="bg-gold text-white"
              type="submit"
            >
              {localization?.SaveAsDraft || "Save as Draft"}
            </Button>

            <Button onClick={openSendDialog} className="bg-gold text-white">
              {mode === "create"
                ? localization?.CreateAndSend || "Create and Send"
                : localization?.UpdateAndSend || "Update and Send"}
            </Button>
          </div>
        </Card>
      </form>
      <SendInvoiceDialog
        emailTo={emailTo}
        setEmailTo={setEmailTo}
        handleSendInvoice={handleSendInvoice}
        sendDialogVisible={sendDialogVisible}
        setSendDialogVisible={setSendDialogVisible}
        emailMessage={emailMessage}
        setEmailMessage={setEmailMessage}
        emailCc={emailCc}
        setEmailCc={setEmailCc}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        withPaymentLink={withPaymentLink}
        setWithPaymentLink={setWithPaymentLink}
        invoiceData={invoiceData}
        total={total}
        setInvoiceData={setInvoiceData}
        action={handleSubmit}
      />
    </>
  );
};
